import { createProfessorLookup } from './professorLookup';
import teachersData from '@/data/teachers_data.json';
import type { ProfessorRatingExtended } from '@/app/types/interfaces';

export type ProfessorRating = ProfessorRatingExtended;

let indexedLookup: ReturnType<typeof createProfessorLookup<(typeof teachersData)[number]>> | undefined;

// Cache for professor ratings to avoid repeated JSON searches
const professorRatingCache = new Map<string, ProfessorRating | null>();
const professorRatingsCache = new Map<string, ProfessorRating[]>();

// Clear cache periodically to prevent memory leaks
if (typeof window !== 'undefined') {
  window.setInterval(() => {
    professorRatingCache.clear();
    professorRatingsCache.clear();
  }, 30 * 60 * 1000); // Clear every 30 minutes
}

/**
 * Find ratings for a single professor
 */
export function findProfessorRating(firstName: string | null, lastName: string): ProfessorRating | null {
  try {
    const cacheKey = `${firstName || ''}|${lastName}`;
    
    // Check cache first
    if (professorRatingCache.has(cacheKey)) {
      return professorRatingCache.get(cacheKey)!;
    }

    indexedLookup ??= createProfessorLookup(teachersData);
    const record = indexedLookup(firstName, lastName);

    let result: ProfessorRating | null = null;
    if (record) {
      result = {
        id: record.id,
        firstName: record.firstName,
        lastName: record.lastName,
        avgDifficulty: record.avgDifficulty,
        avgRating: record.avgRating,
        department: record.department,
        numRatings: record.numRatings,
        wouldTakeAgainPercent: record.wouldTakeAgainPercent,
      };
    }

    // Cache the result
    professorRatingCache.set(cacheKey, result);
    return result;
  } catch (error) {
    console.error('Error finding professor:', error);
    return null;
  }
}

/**
 * Parse instructor name into first and last name
 */
function parseInstructorName(name: string): { firstName: string | null; lastName: string } {
  const trimmedName = name.trim();
  
  // Check if name contains a comma (Last, First format)
  if (trimmedName.includes(',')) {
    const [last, first] = trimmedName.split(',').map(part => part.trim());
    return { firstName: first || null, lastName: last };
  }
  
  // Check if name contains a space (First Last format)
  const parts = trimmedName.split(' ').filter(part => part.length > 0);
  if (parts.length > 1) {
    const lastName = parts[parts.length - 1];
    const firstName = parts.slice(0, -1).join(' ');
    return { firstName, lastName };
  }
  
  // Only one name provided, assume it's the last name
  return { firstName: null, lastName: trimmedName };
}

/**
 * Find ratings for multiple professors from an array of instructor names or a single instructor string
 */
export function findProfessorRatings(instructors: string[] | string | undefined | null): ProfessorRating[] {
  try {
    if (!instructors) {
      return [];
    }

    // Create cache key from instructors array
    const instructorArray = Array.isArray(instructors) 
      ? instructors 
      : typeof instructors === 'string'
        ? instructors.split(';').map(name => name.trim()).filter(name => name.length > 0)
        : [];
    
    const cacheKey = [...instructorArray].sort().join('|');
    
    // Check cache first
    if (professorRatingsCache.has(cacheKey)) {
      return professorRatingsCache.get(cacheKey)!;
    }

    const ratings: ProfessorRating[] = [];

    // Find ratings for each instructor
    for (const instructorName of instructorArray) {
      if (!instructorName) {
        continue;
      }

      const { firstName, lastName } = parseInstructorName(instructorName);

      const rating = findProfessorRating(firstName, lastName);
      if (rating) {
        ratings.push(rating);
      }
    }

    // Cache the result
    professorRatingsCache.set(cacheKey, ratings);
    return ratings;
  } catch (error) {
    console.error('Error finding professor ratings:', error);
    return [];
  }
}
